import React, { Suspense, useEffect, useRef, useState } from 'react';

import { LoginModal, Props as LoginModalProps } from './LoginModal';
import {
  ModalPage,
  PartialClickTrackingOptions,
  PartialConfigurableText,
  RegistrationParams,
} from './types';
import {
  lookupModalVariant,
  mergeConfiguredText,
  substituteWorkContext,
} from './modalVariantRegistry';
import { abTest as bucketLoswpModalAutotest } from 'js/ab_tests/tests/loswp_modal_autotest_2026_q2_v4';
import Cookies from 'js-cookie';
import routes from 'js/core_pre_webpack/routes';

type LoginModalEventDetail = {
  postLoginRedirectUrl?: string;
  forcePostLoginRedirectUrl?: boolean;
  clickTrackingContext?: PartialClickTrackingOptions;
  configuredText?: PartialConfigurableText;
  registrationParams: RegistrationParams;
  initialPage?: ModalPage;
  // Caller-supplied work context. Autotest variants whose configuredText
  // contains a `{paperTitle}` or `{authorName}` token will have it
  // substituted with these values (truncated/formatted by the caller).
  // Absent or empty falls back to a generic phrase. See
  // substituteWorkContext in modalVariantRegistry.
  workTitle?: string;
  authorName?: string;
  // Additional raw work context for fork variants that render a rich
  // "Your paper" card (h4/h5/h6). All optional; callers without work
  // context (e.g. FigureMoreActionsMenu) get title-only degradation.
  workThumbnailUrl?: string;
  workYear?: number;
  workViewCount?: number;
};

// # Usage
//
// It isn't necessary to use openLoginModal directly. The LoginModalManager will listen for
// the 'open-login-modal' event and open the modal when it is triggered. Example:
//
// document.dispatchEvent(new CustomEvent('open-login-modal', {
//   detail: {
//     postLoginRedirectUrl: '/dashboard',
//     configuredText: {
//       buttonsPage: {
//         header: 'Custom welcome message'
//       }
//     },
//     registrationParams: {
//       user_source: 'header_button'
//     }
//   }
// }));
//
export const openLoginModal = (detail: LoginModalEventDetail) => {
  document.dispatchEvent(new CustomEvent('open-login-modal', { detail }));
};

export const LoginModalManager: React.FC = () => {
  const [modalProps, setModalProps] = useState<LoginModalProps>({
    open: false,
    registrationParams: {
      user_source: 'unknown', // user_source is required in event so this should never actually be sent
    },
  });

  // Resolved once on mount from the loswp_modal_autotest_2026_q2_v4 bucket;
  // drives variant lookup at modal-open time.
  const modalVariantRef = useRef<string>('control');

  useEffect(() => {
    const variant = bucketLoswpModalAutotest();
    modalVariantRef.current = variant;

    // Trigger the bucketed fork's dynamic import now so the chunk
    // loads in the background, well before any CTA click. The browser
    // caches the module; React.lazy resolves from cache on render.
    const entry = lookupModalVariant(variant);
    void entry.forkLoader?.();
  }, []);

  useEffect(() => {
    const handleOpenLoginModal = (
      event: CustomEventInit<LoginModalEventDetail>,
    ) => {
      const { detail } = event;
      if (!detail) {
        throw new Error('No detail provided to open-login-modal event');
      }

      if (
        !document.querySelector<HTMLMetaElement>("[name='csrf-token']") &&
        !Cookies.get('masked_csrf_token')
      ) {
        window.location.href = routes.login_path({
          post_login_redirect_url: encodeURIComponent(window.location.href),
          modal_fallback: 'true',
        });
      } else {
        const variantEntry = lookupModalVariant(modalVariantRef.current);
        setModalProps(() => ({
          open: true,
          postLoginRedirectUrl: detail.postLoginRedirectUrl,
          forcePostLoginRedirectUrl: detail.forcePostLoginRedirectUrl,
          clickTrackingContext: detail.clickTrackingContext,
          configuredText: substituteWorkContext(
            mergeConfiguredText(
              detail.configuredText,
              variantEntry.configuredText,
            ),
            { workTitle: detail.workTitle, authorName: detail.authorName },
          ),
          registrationParams: detail.registrationParams,
          initialPage: detail.initialPage,
          workTitle: detail.workTitle,
          authorName: detail.authorName,
          workThumbnailUrl: detail.workThumbnailUrl,
          workYear: detail.workYear,
          workViewCount: detail.workViewCount,
        }));
      }
    };

    document.addEventListener('open-login-modal', handleOpenLoginModal);

    return () => {
      document.removeEventListener('open-login-modal', handleOpenLoginModal);
    };
  }, []);

  const handleClose = () => {
    setModalProps((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const variantEntry = lookupModalVariant(modalVariantRef.current);
  const ForkComponent = variantEntry.forkComponent;

  if (ForkComponent) {
    // Fallback is null (not the default modal) so a click before the fork
    // chunk arrives renders nothing momentarily rather than briefly
    // showing control and then swapping to the variant — which would
    // actively undermine the test signal for structural variants
    // (cluster F).
    return (
      <Suspense fallback={null}>
        <ForkComponent {...modalProps} onClose={handleClose} />
      </Suspense>
    );
  }

  return <LoginModal {...modalProps} onClose={handleClose} />;
};
